// YERİ: src/app/domains/website/sections/mkt-slider.ts
//
// Yatay slayt kabı. Özellik kartları ve hizmet kartları bunun içinde yaşar.
//
// KRİTİK — SSR / SEO: slaytların HEPSİ DOM'da durur. Yalnız görünür olanı
// render eden bir carousel, sayfanın içeriğinin dörtte üçünü HTML'den siler ve
// arama motoru da yapay zekâ da onları hiç görmez. Burada kaydırma CSS ile
// yapılır (scroll-snap): sunucu HTML'i tam, JS kapalıysa bile kullanıcı elle
// kaydırabilir, ve oklar yalnız bir kolaylık katmanıdır.
//
// Bir sonraki kart bilerek KENARDAN GÖZÜKÜR: "devamı var" sinyali, ok
// butonlarından çok daha güçlü.

import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { LanguageStore } from '../core/language.store';
import { a11yLabels } from './mkt-a11y';
import { MktIcon } from './mkt-icon';

/** Bu kadar pikselden fazla kaydıysa niyet sürüklemekti, tıklamak değil. */
const DRAG_THRESHOLD = 6;

/** Bir slaytın bu kadarını geçen sürükleme, o slayta ilerlemek demektir. */
const ADVANCE_RATIO = 0.15;

/** Slayttan slayta geçiş süresi (ms). Tarayıcının kendi 'smooth'una yakın. */
const SCROLL_DURATION = 420;

@Component({
  selector: 'mkt-slider',
  standalone: true,
  imports: [MktIcon],
  template: `
    <div class="relative">
      <!-- Kaydırma rayı. snap-mandatory: kart hep hizalı durur, arada kalmaz. -->
      <!-- snap / scroll-behavior Tailwind sınıflarıyla DEĞİL, marketing.css'te —
           bkz. oradaki .mkt-rail notu (utility'ler !important basılıyor). -->
      <!-- 'is-dragging' sınıfı bağlama ile DEĞİL, elle eklenir: metin seçimi
           pointerdown'ın varsayılan davranışıyla anında başlar, bağlama ise bir
           değişiklik denetimi geç kalır ve kullanıcı yazıyı maviye boyar. -->
      <div #rail
        class="mkt-rail flex gap-5 overflow-x-auto pb-2"
        [class.cursor-grab]="count() > 1 && !dragging()"
        [class.cursor-grabbing]="dragging()"
        (scroll)="onScroll()"
        (pointerdown)="onPointerDown($event)"
        (pointermove)="onPointerMove($event)"
        (pointerup)="onPointerUp($event)"
        (pointercancel)="onPointerUp($event)"
        (dragstart)="$event.preventDefault()">
        <ng-content />
      </div>

      @if (count() > 1) {
        <div class="mt-8 flex items-center justify-between gap-6">
          <!-- Noktalar: kaç slayt var ve neredeyiz. -->
          <ul class="flex items-center gap-2">
            @for (dot of dots(); track $index; let i = $index) {
              <li>
                <button type="button"
                  class="block h-1.5 rounded-full transition-all duration-300"
                  [class]="i === active()
                    ? 'w-7 bg-[color:var(--mkt-accent)]'
                    : 'w-1.5 bg-[color:var(--mkt-border)] hover:bg-[color:var(--mkt-text-faint)]'"
                  [attr.aria-label]="slideLabel(i)"
                  [attr.aria-current]="i === active()"
                  (click)="goTo(i)"></button>
              </li>
            }
          </ul>

          <!-- Ok YÖNÜ yazma yönüne bağlı: RTL'de "sonraki" SOLA bakar.
               'arrow' ikonu sağa bakan tek bir çizim, ters çevirmesi bizde. -->
          <div class="flex gap-2">
            <button type="button" class="mkt-arrow" [disabled]="active() === 0"
              [attr.aria-label]="labels().previous" (click)="step(-1)">
              <mkt-icon name="arrow" [size]="17" [class.rotate-180]="!rtl()" />
            </button>
            <button type="button" class="mkt-arrow" [disabled]="active() >= count() - 1"
              [attr.aria-label]="labels().next" (click)="step(1)">
              <mkt-icon name="arrow" [size]="17" [class.rotate-180]="rtl()" />
            </button>
          </div>
        </div>
      }
    </div>
  `,
  // NOT: .mkt-rail / .mkt-arrow stilleri BURADA DEĞİL, styles/marketing.css'te.
  // Slaytlar <ng-content> ile projeksiyonla geliyor ve projeksiyon içeriği ÜST
  // bileşenin stil kapsamını taşır — burada yazılan `.mkt-rail > *` seçicisi o
  // kartlara hiç uygulanmaz. (Tam olarak bu oldu: dört slayt yan yana sıkıştı.)
})
export class MktSlider implements AfterViewInit {
  /** Kaç slayt var — nokta sayısı buradan. İçerik <ng-content> ile geldiği için sayılamaz. */
  readonly count = input<number>(0);

  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private destroyRef = inject(DestroyRef);
  private language = inject(LanguageStore);
  private rail = viewChild.required<ElementRef<HTMLElement>>('rail');

  /** Ekran okuyucu etiketleri — sayfanın dilinde, bileşende sabit değil. */
  protected labels = computed(() => a11yLabels(this.language.current()));
  protected rtl = computed(() => this.language.dir() === 'rtl');

  protected active = signal(0);
  protected dragging = signal(false);
  protected dots = computed(() => Array.from({ length: this.count() }));

  /** Fare basılı ama henüz sürükleme eşiği aşılmadı — tıklama da olabilir. */
  private armed = false;

  /** Sürükleme başlangıcı — fare konumu, rayın kaydırması ve o anki slayt. */
  private startX = 0;
  private startScroll = 0;
  private startIndex = 0;
  /** Bu sürüklemede kat edilen en büyük mesafe; tıklamayı bastırmak için. */
  private travelled = 0;

  /** Süren geçiş animasyonunun karesi — yenisi başlarken iptal edilir. */
  private animation = 0;

  /** Animasyon hiç ilerlemezse devreye giren emniyet supabı. */
  private animationGuard: ReturnType<typeof setTimeout> | undefined;

  /**
   * Sürükleme bittiğinde tarayıcı, farenin bırakıldığı kartta bir `click`
   * üretir — kullanıcı sadece kaydırmak isterken kartın linkine gitmiş olurdu.
   * Bunu YAKALAMA (capture) evresinde kesmemiz gerekiyor: routerLink kendi
   * dinleyicisini <a>'nın üstünde çalıştırır, yani kabarma evresine kalırsak
   * gezinme çoktan başlamış olur.
   *
   * Angular şablonunda capture evresi bağlanamadığı için elle ekliyoruz.
   */
  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    const el = this.rail().nativeElement;
    const suppress = (event: MouseEvent) => {
      if (this.travelled <= DRAG_THRESHOLD) return;
      event.preventDefault();
      event.stopPropagation();
      this.travelled = 0;
    };

    el.addEventListener('click', suppress, { capture: true });
    this.destroyRef.onDestroy(() => {
      el.removeEventListener('click', suppress, { capture: true });
      // Süren geçiş animasyonu, yok edilmiş bir bileşenin rayına yazmasın.
      cancelAnimationFrame(this.animation);
      clearTimeout(this.animationGuard);
    });
  }

  /**
   * Yalnız FARE sürüklenir. Dokunmatikte tarayıcının kendi kaydırması —
   * momentumuyla, lastik bandıyla — her zaman daha iyidir; ona karışmıyoruz.
   *
   * Burada sürükleme HENÜZ BAŞLAMAZ, sadece hazırlanır. Sebebi önemli:
   * `setPointerCapture` çağrıldığı andan itibaren tarayıcı `click` olayını da
   * yakalayan elemana (raya) hedefler — slaytın içindeki <a>'ya değil. Basit
   * bir tıklama böylece routerLink'e hiç ulaşmaz ve kartlardaki linkler ölür.
   * (Tam olarak bu oldu.) O yüzden yakalamayı, fare gerçekten kaydığında
   * (onPointerMove) yapıyoruz.
   */
  protected onPointerDown(event: PointerEvent): void {
    if (!this.isBrowser || event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.count() < 2) return;

    const el = this.rail().nativeElement;

    this.armed = true;
    this.travelled = 0;
    this.startX = event.clientX;
    this.startScroll = el.scrollLeft;
    this.startIndex = this.active();
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.armed) return;

    const el = this.rail().nativeElement;
    const dx = event.clientX - this.startX;

    this.travelled = Math.max(this.travelled, Math.abs(dx));

    // Eşiği geçene kadar bu hâlâ bir tıklama olabilir — dokunma.
    if (this.travelled <= DRAG_THRESHOLD) return;

    if (!this.dragging()) {
      this.dragging.set(true);
      el.setPointerCapture(event.pointerId);

      // 'is-dragging' snap'i, yumuşak kaydırmayı ve metin seçimini kapatır
      // (marketing.css). Inline stille yapılamaz: Tailwind utility'leri
      // !important basıldığı için inline stil onlara YENİLİR.
      el.classList.add('is-dragging');
    }

    el.scrollLeft = this.startScroll - dx;
  }

  /**
   * Bırakınca hangi slayta oturulacak?
   *
   * "En yakın slaytın merkezine git" YETMEZ: bir slayt neredeyse tüm rayı
   * kapladığı için ilerlemek yarım kart (~570px) sürüklemeyi gerektirirdi ve
   * makul bir sürükleme geri kayardı. Kullanıcının beklediği şey bu değil.
   *
   * Bunun yerine niyete bakıyoruz: kaç slayt boyu kat edildi, ve kalan kesir
   * eşiği aştı mı. Küçük ama kararlı bir sürükleme bir slayt ilerletir; birden
   * çok slayt boyu sürükleme o kadar ilerletir; tereddütlü bir kıpırdanma
   * başladığı yerde kalır.
   */
  protected onPointerUp(event: PointerEvent): void {
    this.armed = false;
    if (!this.dragging()) return; // eşik aşılmadı → bu bir tıklamaydı, karışma

    const el = this.rail().nativeElement;

    this.dragging.set(false);
    el.releasePointerCapture(event.pointerId);

    const moved = el.scrollLeft - this.startScroll;
    const stride = this.stride(el);

    // stride RTL'de NEGATİF gelir (0. slayt sağdadır), bölme de işareti
    // kendiliğinden düzeltir: fiziksel olarak sağa kaydırmak LTR'de ileri,
    // RTL'de geri gitmektir.
    const slides = moved / stride;
    let delta = Math.trunc(slides);

    // Kalan kesir BİRİMSİZ karşılaştırılır. Eskiden stride ile çarpılıp
    // 'stride * ADVANCE_RATIO' ile kıyaslanıyordu; RTL'de stride negatif
    // olduğu için eşik negatife düşer ve koşul HER ZAMAN doğru olurdu —
    // her sürüklemede bir fazla slayt atlanırdı.
    const remainder = slides - delta;
    if (Math.abs(remainder) > ADVANCE_RATIO) delta += Math.sign(remainder);

    const target = Math.min(Math.max(this.startIndex + delta, 0), this.count() - 1);

    // goTo snap'i kendi kapatır ve oturunca ikisini birden geri açar —
    // 'is-dragging' de orada kalkıyor, burada ayrıca çağırmak gerekmiyor.
    this.goTo(target);
  }

  /**
   * Kaydırmayı KENDİMİZ canlandırıyoruz, tarayıcıya bırakmıyoruz.
   *
   * Sebep: RTL'de (Arapça, Farsça) Chrome yumuşak programatik kaydırmayı hiç
   * yapmıyor. `scrollTo`/`scrollBy` `behavior:'smooth'` ile çağrıldığında ray
   * tek piksel kımıldamıyor; aynı hedefe anlık atama ise sorunsuz çalışıyor.
   * Yani oklar ve noktalar RTL'de tamamen ölüydü. Her karede scrollLeft'i
   * kendimiz yazınca davranış iki yönde de aynı oluyor.
   *
   * Snap ve tarayıcı yumuşatması animasyon boyunca kapalı ('is-seeking');
   * bitince ikisi de geri gelir ve ray zaten bir snap noktasında olur.
   */
  private animateScrollTo(el: HTMLElement, target: number): void {
    cancelAnimationFrame(this.animation);
    clearTimeout(this.animationGuard);

    const from = el.scrollLeft;
    const distance = target - from;

    const settle = () => {
      cancelAnimationFrame(this.animation);
      clearTimeout(this.animationGuard);
      el.classList.remove('is-seeking');
      el.classList.remove('is-dragging');
    };

    const jump = () => {
      el.scrollLeft = target;
      settle();
    };

    // Zaten oradayız — animasyona gerek yok, ama snap yine de geri açılmalı.
    if (Math.abs(distance) < 1) {
      settle();
      return;
    }

    // Hareketi azaltma tercihi: animasyon yok, doğrudan hedefe.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      jump();
      return;
    }

    let startedAt = 0;
    const stepFrame = (now: number) => {
      startedAt ||= now;

      const progress = Math.min((now - startedAt) / SCROLL_DURATION, 1);
      // ease-out kübik: hızlı başlar, yumuşak durur — snap hissine yakın.
      el.scrollLeft = from + distance * (1 - (1 - progress) ** 3);

      if (progress < 1) this.animation = requestAnimationFrame(stepFrame);
      else settle();
    };

    this.animation = requestAnimationFrame(stepFrame);

    // Emniyet supabı: requestAnimationFrame arka plan sekmesinde HİÇ çalışmaz.
    // O sekmeye tıklama gelmez ama gezinme gelebilir; animasyon yarıda kalırsa
    // ray tuhaf bir yerde ve snap kapalı kalırdı. Süre dolduğunda hâlâ
    // bitmemişse hedefe atlayıp snap'i geri açıyoruz.
    this.animationGuard = setTimeout(jump, SCROLL_DURATION + 300);
  }

  /** İki slayt arası mesafe (kart genişliği + boşluk). */
  private stride(el: HTMLElement): number {
    const first = el.children[0] as HTMLElement | undefined;
    const second = el.children[1] as HTMLElement | undefined;
    if (!first) return el.clientWidth || 1;

    return second ? second.offsetLeft - first.offsetLeft : first.clientWidth || 1;
  }

  /**
   * Hedef slaytı hizalar.
   *
   * MUTLAK konum yerine GÖRECELİ kaydırma (scrollBy) kullanılıyor: `offsetLeft`
   * daima soldan ölçülür, `scrollLeft` ise RTL'de negatif aralıkta çalışır.
   * İkisini karıştıran eski hesap Arapça/Farsça'da hep 0'a kırpılıyordu, yani
   * oklar ve noktalar hiçbir şey yapmıyordu. Rect farkı iki yönde de doğrudur.
   *
   * Kaydırma boyunca snap KAPALI tutulur ('is-seeking'): açıkken Chrome
   * programatik yumuşak kaydırmayı çoğu zaman hiç başlatmıyor ve ray olduğu
   * yerde kalıyor — LTR'de de, RTL'de de. Snap, kaydırma oturunca geri gelir.
   */
  protected goTo(index: number): void {
    if (!this.isBrowser) return;

    const el = this.rail().nativeElement;
    const slide = el.children[index] as HTMLElement | undefined;
    if (!slide) return;

    const delta = slide.getBoundingClientRect().left - el.getBoundingClientRect().left;

    // Hedef indeksi BURADA yazıyoruz, kaydırma olayını beklemeden: ok ve
    // noktalarla giderken niyet zaten belli. Yalnız `onScroll`a güvenmek,
    // olayın gecikmesi ya da hiç gelmemesi hâlinde noktayı ve ok butonlarının
    // pasifliğini yanlış slaytta bırakıyordu. Parmakla kaydırmada değer yine
    // `onScroll`dan gelir ve aynı sonucu hesaplar.
    this.active.set(index);

    el.classList.add('is-seeking');
    this.animateScrollTo(el, el.scrollLeft + delta);
  }

  /**
   * Nokta butonunun ekran okuyucu adı.
   *
   * Rakam da çevrilir: Farsça ۱۲۳ ve Arapça ١٢٣ kendi rakamlarını kullanır ve
   * ekran okuyucu bunları o dilin sayısı olarak okur. Latin '1' bırakmak,
   * cümlenin ortasında dil değiştirmek demekti.
   */
  protected slideLabel(index: number): string {
    const n = (index + 1).toLocaleString(this.language.current() || 'en');
    return this.labels().slide.replace('{n}', n);
  }

  protected step(delta: number): void {
    const next = Math.min(Math.max(this.active() + delta, 0), this.count() - 1);
    this.goTo(next);
  }

  /**
   * Aktif slaytı KAYDIRMA KONUMUNDAN türetir, tıklamadan değil — kullanıcı
   * parmakla da kaydırabilir (mobilde asıl yol bu) ve noktalar yine doğru kalır.
   */
  protected onScroll(): void {
    const el = this.rail().nativeElement;
    const children = Array.from(el.children) as HTMLElement[];
    if (children.length === 0) return;

    // Merkezler EKRAN koordinatında karşılaştırılır (getBoundingClientRect):
    // scrollLeft ile offsetLeft'i karıştırmak RTL'de yanlış slaytı aktif
    // gösteriyordu — biri negatif aralıkta, diğeri hep soldan ölçülü.
    const railRect = el.getBoundingClientRect();
    const center = railRect.left + railRect.width / 2;

    let nearest = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    children.forEach((child, index) => {
      const rect = child.getBoundingClientRect();
      const childCenter = rect.left + rect.width / 2;
      const distance = Math.abs(childCenter - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = index;
      }
    });

    this.active.set(nearest);
  }
}
